import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import soundAlertService from '../services/soundAlertService';
import { SOUND_CONSTANTS, SOUND_TYPES } from '../constants/soundConstants';

const SoundSettingsContext = createContext();

export const useSoundSettings = () => {
  const context = useContext(SoundSettingsContext);
  if (!context) {
    throw new Error('useSoundSettings must be used within a SoundSettingsProvider');
  }
  return context;
};

export const SoundSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    isEnabled: true,
    volume: SOUND_CONSTANTS.DEFAULT_VOLUME,
    tabSettings: {
      alerts: { enabled: true, volume: SOUND_CONSTANTS.DEFAULT_ALERTS_VOLUME },
      inbox: { enabled: true, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME },
      my: { enabled: true, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME },
      all: { enabled: false, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME },
      pin: { enabled: false, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME }
    }
  });

  // Load settings on mount and subscribe to cross-tab changes
  useEffect(() => {
    // Load initial settings
    const savedSettings = soundAlertService.getSettings();
    setSettings(savedSettings);
    
    // Initialize cross-tab synchronization immediately (not lazy)
    // This ensures all tabs are ready to receive updates from the start
    soundAlertService.initCrossTabSync();
    
    // Track last update timestamp to prevent duplicate updates
    let lastUpdateTimestamp = 0;
    const UPDATE_DEBOUNCE_MS = SOUND_CONSTANTS.SETTINGS_UPDATE_DEBOUNCE_MS;
    
    // Subscribe to settings changes from service events
    // Service handles both BroadcastChannel and storage events internally
    const handleSettingsChanged = (newSettings) => {
      const now = Date.now();
      // Debounce to prevent duplicate updates
      if (now - lastUpdateTimestamp < UPDATE_DEBOUNCE_MS) {
        return;
      }
      lastUpdateTimestamp = now;
      setSettings(newSettings);
    };
    
    soundAlertService.on('settings-changed', handleSettingsChanged);
    
    // Cleanup: unsubscribe on unmount
    return () => {
      soundAlertService.off('settings-changed', handleSettingsChanged);
    };
  }, []);

  // Cleanup on component unmount and page unload
  useEffect(() => {
    let isCleaningUp = false; // Flag to prevent redundant cleanup calls

    // Handle page unload - use destroy() for complete cleanup
    const handleBeforeUnload = () => {
      if (!isCleaningUp) {
        isCleaningUp = true;
        // Use destroy() for complete cleanup on page unload
        soundAlertService.destroy();
      }
    };

    // Add event listeners for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    // Cleanup function - runs when component unmounts
    return () => {
      if (!isCleaningUp) {
        isCleaningUp = true;
        
        // Cancel all pending sounds on unmount
        soundAlertService.cancelAllSounds();
        
        // Remove event listeners
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('pagehide', handleBeforeUnload);
        
        // Note: We don't call destroy() here because the service is a singleton
        // that may be used across route changes. destroy() is only called on
        // actual page unload (beforeunload/pagehide events)
        // 
        // Note: We intentionally do NOT cancel sounds on visibility change
        // (tab switching) because we want sounds to play when the user is on
        // another tab to alert them of new messages.
      }
    };
  }, []);

  // Update settings - memoized to prevent unnecessary re-renders
  // Uses functional setState to avoid dependency on settings object
  const updateSettings = useCallback((newSettings) => {
    setSettings((prevSettings) => {
      const updatedSettings = { ...prevSettings, ...newSettings };
      soundAlertService.updateSettings(updatedSettings);
      return updatedSettings;
    });
  }, []); // Empty deps - uses functional setState pattern

  // Toggle sound on/off - memoized with stable reference
  const toggleSound = useCallback(() => {
    setSettings((prevSettings) => {
      const updatedSettings = { ...prevSettings, isEnabled: !prevSettings.isEnabled };
      soundAlertService.updateSettings(updatedSettings);
      return updatedSettings;
    });
  }, []); // Empty deps - uses functional setState pattern

  // Update volume - memoized with stable reference
  const updateVolume = useCallback((volume) => {
    const clampedVolume = Math.max(SOUND_CONSTANTS.MIN_VOLUME, Math.min(SOUND_CONSTANTS.MAX_VOLUME, volume));
    setSettings((prevSettings) => {
      const updatedSettings = { ...prevSettings, volume: clampedVolume };
      soundAlertService.updateSettings(updatedSettings);
      return updatedSettings;
    });
  }, []); // Empty deps - uses functional setState pattern

  // Test sound - memoized with stable reference (no dependencies)
  // Supports both global and tab-specific testing
  const testSound = useCallback((type = SOUND_TYPES.NEW_MESSAGE, tabName = null) => {
    soundAlertService.testSound(type, tabName);
  }, []); // Empty deps - calls service directly

  // Play notification sound - memoized with stable reference
  const playNotificationSound = useCallback((type = SOUND_TYPES.NEW_MESSAGE, tabName = null) => {
    soundAlertService.playNotificationSound(type, tabName);
  }, []); // Empty deps - calls service directly

  // Per-tab sound management - all memoized with stable references
  const toggleTabSound = useCallback((tabName) => {
    soundAlertService.toggleTabSound(tabName);
    // Get updated settings from service and update state
    setSettings(soundAlertService.getSettings());
  }, []); // Empty deps - calls service directly

  const updateTabVolume = useCallback((tabName, volume) => {
    soundAlertService.setTabVolume(tabName, volume);
    // Get updated settings from service and update state
    setSettings(soundAlertService.getSettings());
  }, []); // Empty deps - calls service directly

  // Get tab settings - memoized with stable reference (pure function)
  const getTabSettings = useCallback((tabName) => {
    return soundAlertService.getTabSettings(tabName);
  }, []); // Empty deps - calls service directly, returns current state

  // Get all tab settings - memoized with stable reference (pure function)
  const getAllTabSettings = useCallback(() => {
    return soundAlertService.getAllTabSettings();
  }, []); // Empty deps - calls service directly, returns current state

  // Memoize context value to prevent unnecessary re-renders of all consumers
  // Only recreate when settings actually change
  // Optimized: Removed stable functions from dependency array (they're memoized with empty deps)
  const value = useMemo(() => ({
    settings,
    updateSettings,
    toggleSound,
    updateVolume,
    testSound,
    playNotificationSound,
    toggleTabSound,
    updateTabVolume,
    getTabSettings,
    getAllTabSettings
  }), [
    settings // Only recreate when settings change
    // All functions are stable (memoized with empty deps), so they don't need to be in deps
    // This eliminates redundant dependencies and makes the code cleaner
  ]);

  return (
    <SoundSettingsContext.Provider value={value}>
      {children}
    </SoundSettingsContext.Provider>
  );
};
